import { useState } from "react";
import { format, parseISO, differenceInCalendarDays } from "date-fns";
import { Bell, X } from "lucide-react";
import { COLORS } from "@/lib/api";
import { CheckCircle } from "@/components/shared";

const KEY = "bb-reminder-dismissed";

export const TaskReminders = ({ todos, onToggle }) => {
  const today = format(new Date(), "yyyy-MM-dd");
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(KEY) === today);
  const due = todos
    .flatMap((l) => l.items.filter((i) => !i.done && i.due_date && i.due_date <= today).map((i) => ({ ...i, list: l })))
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
  if (dismissed || !due.length) return null;
  const overdue = due.filter((t) => t.due_date < today).length;
  const dueToday = due.length - overdue;
  const dismiss = () => {
    sessionStorage.setItem(KEY, today);
    setDismissed(true);
  };
  const label = (d) => {
    const n = differenceInCalendarDays(new Date(), parseISO(d));
    return n === 0 ? "Today" : n === 1 ? "Yesterday" : `${n} days ago`;
  };

  return (
    <div data-testid="task-reminders" className="relative mb-8 rounded-2xl border border-t-primary-line bg-t-primary-soft/70 p-5 sm:p-6 fade-up">
      <button data-testid="task-reminders-dismiss" onClick={dismiss} className="absolute top-4 right-4 p-1.5 rounded-lg text-t-muted hover:bg-t-card/70 transition-colors">
        <X className="h-4 w-4" />
      </button>
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 shrink-0 rounded-xl bg-t-primary text-white flex items-center justify-center">
          <Bell className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <p className="font-serif-display text-2xl font-semibold leading-tight">A gentle reminder</p>
          <p className="text-sm text-t-muted mt-0.5" data-testid="task-reminders-summary">
            {dueToday > 0 && `${dueToday} task${dueToday > 1 ? "s" : ""} due today`}
            {dueToday > 0 && overdue > 0 && " · "}
            {overdue > 0 && `${overdue} waiting from earlier`}. No rush — one at a time.
          </p>
          <ul className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2">
            {due.slice(0, 6).map((t) => (
              <li key={t.id} data-testid={`reminder-task-${t.id}`} className="flex items-center gap-3 bg-t-card rounded-xl px-3 py-2.5 border border-t-line">
                <CheckCircle checked={false} onClick={() => onToggle(t)} color={COLORS[t.list.color]} testId={`reminder-task-check-${t.id}`} />
                <span className="flex-1 text-sm truncate">{t.text}</span>
                <span className={`text-[11px] font-medium ${t.due_date < today ? "text-t-primary-ink" : "text-t-c2-ink"}`}>{label(t.due_date)}</span>
              </li>
            ))}
          </ul>
          {due.length > 6 && <p className="text-xs text-t-muted mt-2">+{due.length - 6} more on your to-do lists</p>}
        </div>
      </div>
    </div>
  );
};
